/**
 * The analytics opt-out preference.
 *
 * Two signals, one meaning. When either is set, the Tag Manager loader
 * in the document head returns before it ever creates the script tag —
 * so opting out means the container is never loaded, not that it loads
 * and is asked politely to stay quiet.
 *
 * The authoritative copy of that check is a *string* built by
 * `seo/tagManager.ts`, because it has to run inline in the head before
 * the bundle exists. This module is the same logic for the React side
 * (the toggle on /privacy), and owns the storage key both share.
 *
 * `localStorage`, deliberately, not `sessionStorage`: `lib/bootCleanup.ts`
 * clears the session store on every boot, and a preference that had to be
 * re-set on each page load would not be a preference. bootCleanup only
 * reaps the specific `revetra.session` key, so this one survives.
 */
export const ANALYTICS_OPT_OUT_KEY = "revetra.analytics-optout";
export const ANALYTICS_OPT_OUT_VALUE = "1";

/**
 * Global Privacy Control — the browser-level opt-out signal California
 * recognises. Honoured unconditionally and without a UI: a visitor who
 * has set it in their browser has already answered the question.
 */
export function hasGlobalPrivacyControl(): boolean {
  if (typeof navigator === "undefined") return false;
  return navigator.globalPrivacyControl === true;
}

/** True when this browser has opted out by either signal. */
export function isOptedOut(): boolean {
  if (hasGlobalPrivacyControl()) return true;
  try {
    return window.localStorage.getItem(ANALYTICS_OPT_OUT_KEY) === ANALYTICS_OPT_OUT_VALUE;
  } catch {
    // Private mode can throw on access. Failing closed here matches the
    // inline loader, which also treats an unreadable store as opted out.
    return true;
  }
}

/**
 * Records the preference. Returns false when storage is unavailable, so
 * the caller can say so rather than showing a toggle that silently
 * forgets. Nothing here unloads an already-running container — the
 * caller reloads the document, which is what actually makes it take
 * effect.
 */
export function setOptOut(optedOut: boolean): boolean {
  try {
    if (optedOut) {
      window.localStorage.setItem(ANALYTICS_OPT_OUT_KEY, ANALYTICS_OPT_OUT_VALUE);
    } else {
      window.localStorage.removeItem(ANALYTICS_OPT_OUT_KEY);
    }
    return true;
  } catch {
    return false;
  }
}
